import { getV3ApiBaseUrl } from "./constants";

// Access/refresh token handling for the v3 auth API.
//
// Both tokens live inside the `user-details` localStorage entry (`token` and
// `refreshToken`), next to the profile saved at login. When a request comes
// back 401 the HTTP layer calls refreshAccessToken() and retries once; a timer
// also refreshes shortly before the access token expires, so long-lived pages
// (realtime sockets, dashboards) keep a valid token.

const USER_DETAILS_KEY = "user-details";
const getRefreshApi = () => `${getV3ApiBaseUrl()}/v3/auth/token/refresh`;

// Refresh this long before expiry. Each tab adds its own random jitter so two
// open tabs do not both spend the same (rotating) refresh token at once.
const REFRESH_LEAD_MS = 60 * 1000;
const REFRESH_JITTER_MS = 20 * 1000;
// setTimeout overflows above ~24.8 days.
const MAX_TIMER_MS = 2147483647;

const readSession = () => {
  try {
    return JSON.parse(localStorage.getItem(USER_DETAILS_KEY)) || null;
  } catch (e) {
    return null;
  }
};

export const getAccessToken = () => readSession()?.token || null;
export const getStoredRefreshToken = () => readSession()?.refreshToken || null;

// Expiry (ms since epoch) from the JWT's own `exp` claim, so it does not
// depend on the unit `expiresIn` is sent in.
const getTokenExpiry = (token) => {
  try {
    const payload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const { exp } = JSON.parse(atob(payload));
    return typeof exp === "number" ? exp * 1000 : null;
  } catch (e) {
    return null;
  }
};

// The refresh response may be flat ({ accessToken, ... }) like login, or
// wrapped JSON:API style ({ data: { attributes: { ... } } }).
const readTokens = (body) => {
  const source = body?.accessToken ? body : body?.data?.attributes || body?.data || {};
  return {
    accessToken: source.accessToken,
    refreshToken: source.refreshToken,
    expiresIn: source.expiresIn,
  };
};

// Writes new tokens into the stored session. Skipped if the user logged out
// (or logged in as someone else) while the refresh was in flight.
const saveTokens = (usedRefreshToken, { accessToken, refreshToken, expiresIn }) => {
  const session = readSession();
  if (!session || session.refreshToken !== usedRefreshToken) return false;

  localStorage.setItem(
    USER_DETAILS_KEY,
    JSON.stringify({
      ...session,
      token: accessToken,
      // Keep the old refresh token if the API does not rotate it.
      refreshToken: refreshToken || session.refreshToken,
      ...(expiresIn !== undefined && { expiresIn }),
    })
  );
  return true;
};

let inFlight = null;

/**
 * Gets a new access token with the stored refresh token. Resolves true when a
 * usable token is now stored, false when the session cannot be refreshed (no
 * refresh token, or the API rejected it) and the user has to log in again.
 *
 * `failedToken` is the access token a request was rejected with. If the stored
 * token has already changed (another request or tab refreshed it), there is
 * nothing to do.
 */
export const refreshAccessToken = (failedToken) => {
  const current = getAccessToken();
  if (failedToken && current && current !== failedToken) return Promise.resolve(true);
  if (inFlight) return inFlight;

  const usedRefreshToken = getStoredRefreshToken();
  if (!usedRefreshToken) return Promise.resolve(false);

  inFlight = (async () => {
    try {
      const response = await fetch(getRefreshApi(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: usedRefreshToken }),
      });

      if (response.ok) {
        const tokens = readTokens(await response.json());
        if (tokens.accessToken && saveTokens(usedRefreshToken, tokens)) {
          scheduleTokenRefresh();
          return true;
        }
      }
    } catch (error) {
      console.warn("Token refresh failed", error);
    } finally {
      inFlight = null;
    }

    // Another tab may have rotated the refresh token first; its new token
    // is in storage now and is good to use.
    const latest = getAccessToken();
    return Boolean(latest && latest !== (failedToken || current));
  })();

  return inFlight;
};

let refreshTimer = null;

// (Re)arms the timer that refreshes the access token just before it expires.
// Call after login; it re-arms itself after every refresh.
export const scheduleTokenRefresh = () => {
  clearTimeout(refreshTimer);
  refreshTimer = null;

  const token = getAccessToken();
  const expiry = token && getStoredRefreshToken() ? getTokenExpiry(token) : null;
  if (!expiry) return;

  const jitter = Math.random() * REFRESH_JITTER_MS;
  const delay = Math.min(Math.max(expiry - Date.now() - REFRESH_LEAD_MS - jitter, 0), MAX_TIMER_MS);

  refreshTimer = setTimeout(() => {
    // Passing the token we planned for means a tab that already refreshed
    // (storage changed) is left alone.
    refreshAccessToken(token);
  }, delay);
};

// Another tab refreshed, logged in or logged out: follow its token.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === USER_DETAILS_KEY || event.key === null) scheduleTokenRefresh();
  });
  scheduleTokenRefresh();
}
